import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Clock } from 'lucide-react';
import {
  AdminClientApi,
  ClientUserInterface,
  ClientBelief,
  ClientThoughtWithAuthor,
  ClientFeed,
} from '../../Admin-ClientLibrary';
import SupportingBeliefsListView from '../../components/SupportingBeliefsListView';
import { getApiUrl } from '../../config/api';
import { useRecentEntities } from '../../contexts/RecentEntitiesContext';
import { RecentEntity } from '../../types/RecentEntities';
import './SupportingBeliefs.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const SupportingBeliefs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { recentEntities } = useRecentEntities();

  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [openRecentDropdown, setOpenRecentDropdown] = useState<string | null>(null);

  // Initialize filters from URL parameters
  const [filters, setFilters] = useState({
    userId: searchParams.get('userId') || '',
    goeUserId: searchParams.get('goeUserId') || '',
    loeUserId: searchParams.get('loeUserId') || '',
    beliefId: searchParams.get('beliefId') || '',
    supportingBeliefId: searchParams.get('supportingBeliefId') || '',
    cacheThoughtId: searchParams.get('cacheThoughtId') || '',
    cachePosition: searchParams.get('cachePosition') || '',
    cacheSupportingThoughtId: searchParams.get('cacheSupportingThoughtId') || '',
    cacheSupportingPosition: searchParams.get('cacheSupportingPosition') || '',
    createdAfter: searchParams.get('createdAfter') || '',
    createdBefore: searchParams.get('createdBefore') || '',
    updatedAfter: searchParams.get('updatedAfter') || '',
    updatedBefore: searchParams.get('updatedBefore') || '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    userId: searchParams.get('userId') || '',
    goeUserId: searchParams.get('goeUserId') || '',
    loeUserId: searchParams.get('loeUserId') || '',
    beliefId: searchParams.get('beliefId') || '',
    supportingBeliefId: searchParams.get('supportingBeliefId') || '',
    cacheThoughtId: searchParams.get('cacheThoughtId') || '',
    cachePosition: searchParams.get('cachePosition') || '',
    cacheSupportingThoughtId: searchParams.get('cacheSupportingThoughtId') || '',
    cacheSupportingPosition: searchParams.get('cacheSupportingPosition') || '',
    createdAfter: searchParams.get('createdAfter') || '',
    createdBefore: searchParams.get('createdBefore') || '',
    updatedAfter: searchParams.get('updatedAfter') || '',
    updatedBefore: searchParams.get('updatedBefore') || '',
  });

  // Close recent dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.recent-dropdown-container')) {
        setOpenRecentDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Function to update URL parameters
  const updateUrlParams = (params: Record<string, string | number | undefined>): void => {
    const newParams = new URLSearchParams();

    // Add all current search params first
    searchParams.forEach((value, key) => {
      newParams.set(key, value);
    });

    // Update with new params
    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== '') {
        newParams.set(key, value.toString());
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  };

  const handleFilterChange = (filterName: string, value: string): void => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  const handleQuickUserFilter = (goeValue: string, loeValue: string): void => {
    setFilters((prev) => ({ ...prev, goeUserId: goeValue, loeUserId: loeValue }));
  };

  // Recent entities helpers
  const getFilteredRecentEntities = (type: 'user' | 'belief' | 'thought'): RecentEntity[] => {
    return recentEntities.filter((entity) => entity.type === type);
  };

  const handleRecentEntityClick = (entity: RecentEntity, filterField: string): void => {
    handleFilterChange(filterField, entity.id);
    setOpenRecentDropdown(null);
  };

  const toggleRecentDropdown = (dropdownType: string): void => {
    setOpenRecentDropdown(openRecentDropdown === dropdownType ? null : dropdownType);
  };

  const applyFilters = (): void => {
    setAppliedFilters({ ...filters });
    updateUrlParams(filters);
    setShowFilters(false);
  };

  const clearFilters = (): void => {
    const clearedFilters = {
      userId: '',
      goeUserId: '',
      loeUserId: '',
      beliefId: '',
      supportingBeliefId: '',
      cacheThoughtId: '',
      cachePosition: '',
      cacheSupportingThoughtId: '',
      cacheSupportingPosition: '',
      createdAfter: '',
      createdBefore: '',
      updatedAfter: '',
      updatedBefore: '',
    };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    updateUrlParams(clearedFilters);
  };

  const getFilterSummary = (): string => {
    const activeFilters: string[] = [];

    if (appliedFilters.userId) activeFilters.push(`User: ${appliedFilters.userId}`);
    if (appliedFilters.goeUserId || appliedFilters.loeUserId) {
      const range = `${appliedFilters.goeUserId || '∞'} - ${appliedFilters.loeUserId || '∞'}`;
      activeFilters.push(`User Range: ${range}`);
    }
    if (appliedFilters.beliefId) activeFilters.push(`Belief: ${appliedFilters.beliefId}`);
    if (appliedFilters.supportingBeliefId)
      activeFilters.push(`Supporting Belief: ${appliedFilters.supportingBeliefId}`);
    if (appliedFilters.cachePosition)
      activeFilters.push(`Position: ${appliedFilters.cachePosition}`);
    if (appliedFilters.cacheSupportingPosition)
      activeFilters.push(`Supporting Position: ${appliedFilters.cacheSupportingPosition}`);
    if (appliedFilters.cacheThoughtId)
      activeFilters.push(`Thought: ${appliedFilters.cacheThoughtId}`);
    if (appliedFilters.cacheSupportingThoughtId)
      activeFilters.push(`Supporting Thought: ${appliedFilters.cacheSupportingThoughtId}`);
    if (appliedFilters.createdAfter || appliedFilters.createdBefore) {
      const dateRange = `${appliedFilters.createdAfter || '∞'} to ${
        appliedFilters.createdBefore || '∞'
      }`;
      activeFilters.push(`Created: ${dateRange}`);
    }
    if (appliedFilters.updatedAfter || appliedFilters.updatedBefore) {
      const dateRange = `${appliedFilters.updatedAfter || '∞'} to ${
        appliedFilters.updatedBefore || '∞'
      }`;
      activeFilters.push(`Updated: ${dateRange}`);
    }

    return activeFilters.length > 0 ? activeFilters.join(' • ') : 'No filters applied';
  };

  // Recent dropdown component
  const RecentDropdown: React.FC<{
    type: 'user' | 'belief' | 'thought';
    dropdownKey: string;
    filterField: string;
    placeholder: string;
  }> = ({ type, dropdownKey, filterField, placeholder }) => {
    const recentItems = getFilteredRecentEntities(type);
    const isOpen = openRecentDropdown === dropdownKey;

    return (
      <div className="recent-dropdown-container">
        <button
          type="button"
          className="recent-dropdown-btn"
          onClick={() => toggleRecentDropdown(dropdownKey)}
        >
          <Clock size={14} />
          {placeholder}
        </button>
        {isOpen && recentItems.length > 0 && (
          <div className="recent-dropdown">
            {recentItems.slice(0, 5).map((entity) => (
              <button
                key={`${entity.type}-${entity.id}`}
                className="recent-item"
                onClick={() => handleRecentEntityClick(entity, filterField)}
              >
                <span className="recent-id">{entity.id}</span>
                <span className="recent-name">
                  {type === 'user' && (entity.data as ClientUserInterface)?.name}
                  {type === 'belief' && `${(entity.data as ClientBelief)?.position} belief`}
                  {type === 'thought' &&
                    (entity.data as ClientThoughtWithAuthor)?.text?.substring(0, 30) + '...'}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Convert filters to the format expected by SupportingBeliefsListView
  const listViewFilters = {
    userId: appliedFilters.userId ? parseInt(appliedFilters.userId) : undefined,
    goeUserId: appliedFilters.goeUserId ? parseInt(appliedFilters.goeUserId) : undefined,
    loeUserId: appliedFilters.loeUserId ? parseInt(appliedFilters.loeUserId) : undefined,
    beliefId: appliedFilters.beliefId ? parseInt(appliedFilters.beliefId) : undefined,
    supportingBeliefId: appliedFilters.supportingBeliefId
      ? parseInt(appliedFilters.supportingBeliefId)
      : undefined,
    cacheThoughtId: appliedFilters.cacheThoughtId
      ? parseInt(appliedFilters.cacheThoughtId)
      : undefined,
    cachePosition: appliedFilters.cachePosition || undefined,
    cacheSupportingThoughtId: appliedFilters.cacheSupportingThoughtId
      ? parseInt(appliedFilters.cacheSupportingThoughtId)
      : undefined,
    cacheSupportingPosition: appliedFilters.cacheSupportingPosition || undefined,
    createdAfter: appliedFilters.createdAfter || undefined,
    createdBefore: appliedFilters.createdBefore || undefined,
    updatedAfter: appliedFilters.updatedAfter || undefined,
    updatedBefore: appliedFilters.updatedBefore || undefined,
  };

  return (
    <div className="supporting-beliefs-page">
      <div className="supporting-beliefs-header">
        <div className="header-top">
          <h1>Supporting Beliefs</h1>
        </div>

        <div className="supporting-beliefs-filters">
          <div className="controls-row">
            <button className="btn-secondary" onClick={() => setShowFilters(!showFilters)}>
              <Filter size={16} />
              Filter{' '}
              {Object.values(appliedFilters).some((f) => f) && (
                <span className="filter-badge">•</span>
              )}
            </button>
          </div>

          <div className="filter-summary">{getFilterSummary()}</div>

          {showFilters && (
            <div className="filters-dropdown">
              <div className="filters-header">
                <h3>Filters</h3>
                <button className="btn-text" onClick={clearFilters}>
                  Clear All
                </button>
              </div>

              <div className="filter-sections">
                <div className="filter-section">
                  <h4>User Filters</h4>

                  <div className="filter-group">
                    <label>User ID:</label>
                    <input
                      type="number"
                      placeholder="Enter User ID"
                      value={filters.userId}
                      onChange={(e) => handleFilterChange('userId', e.target.value)}
                    />
                    <RecentDropdown
                      type="user"
                      dropdownKey="userId"
                      filterField="userId"
                      placeholder="Recent Users"
                    />
                  </div>

                  <div className="filter-group">
                    <label>User ID Range:</label>
                    <div className="range-inputs">
                      <input
                        type="number"
                        placeholder="From"
                        value={filters.goeUserId}
                        onChange={(e) => handleFilterChange('goeUserId', e.target.value)}
                      />
                      <span className="range-separator">to</span>
                      <input
                        type="number"
                        placeholder="To"
                        value={filters.loeUserId}
                        onChange={(e) => handleFilterChange('loeUserId', e.target.value)}
                      />
                    </div>
                    <div className="quick-filters">
                      <button
                        className="btn-quick-filter"
                        onClick={() => handleQuickUserFilter('11', '22')}
                      >
                        Power Users (11-22)
                      </button>
                      <button
                        className="btn-quick-filter"
                        onClick={() => handleQuickUserFilter('1001', '2000')}
                      >
                        AI Users (1001-2000)
                      </button>
                    </div>
                  </div>
                </div>

                <div className="filter-section">
                  <h4>Belief Filters</h4>

                  <div className="filter-group">
                    <label>Belief ID:</label>
                    <input
                      type="number"
                      placeholder="Enter Belief ID"
                      value={filters.beliefId}
                      onChange={(e) => handleFilterChange('beliefId', e.target.value)}
                    />
                    <RecentDropdown
                      type="belief"
                      dropdownKey="beliefId"
                      filterField="beliefId"
                      placeholder="Recent Beliefs"
                    />
                  </div>

                  <div className="filter-group">
                    <label>Supporting Belief ID:</label>
                    <input
                      type="number"
                      placeholder="Enter Supporting Belief ID"
                      value={filters.supportingBeliefId}
                      onChange={(e) => handleFilterChange('supportingBeliefId', e.target.value)}
                    />
                    <RecentDropdown
                      type="belief"
                      dropdownKey="supportingBeliefId"
                      filterField="supportingBeliefId"
                      placeholder="Recent Beliefs"
                    />
                  </div>

                  <div className="filter-group">
                    <label>Position:</label>
                    <select
                      value={filters.cachePosition}
                      onChange={(e) => handleFilterChange('cachePosition', e.target.value)}
                    >
                      <option value="">All Positions</option>
                      <option value="believe">Believe</option>
                      <option value="disbelieve">Disbelieve</option>
                      <option value="unsure">Unsure</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Supporting Position:</label>
                    <select
                      value={filters.cacheSupportingPosition}
                      onChange={(e) =>
                        handleFilterChange('cacheSupportingPosition', e.target.value)
                      }
                    >
                      <option value="">All Positions</option>
                      <option value="believe">Believe</option>
                      <option value="disbelieve">Disbelieve</option>
                      <option value="unsure">Unsure</option>
                    </select>
                  </div>
                </div>

                <div className="filter-section">
                  <h4>Thought Filters</h4>

                  <div className="filter-group">
                    <label>Thought ID:</label>
                    <input
                      type="number"
                      placeholder="Enter Thought ID"
                      value={filters.cacheThoughtId}
                      onChange={(e) => handleFilterChange('cacheThoughtId', e.target.value)}
                    />
                    <RecentDropdown
                      type="thought"
                      dropdownKey="cacheThoughtId"
                      filterField="cacheThoughtId"
                      placeholder="Recent Thoughts"
                    />
                  </div>

                  <div className="filter-group">
                    <label>Supporting Thought ID:</label>
                    <input
                      type="number"
                      placeholder="Enter Supporting Thought ID"
                      value={filters.cacheSupportingThoughtId}
                      onChange={(e) =>
                        handleFilterChange('cacheSupportingThoughtId', e.target.value)
                      }
                    />
                    <RecentDropdown
                      type="thought"
                      dropdownKey="cacheSupportingThoughtId"
                      filterField="cacheSupportingThoughtId"
                      placeholder="Recent Thoughts"
                    />
                  </div>
                </div>

                <div className="filter-section">
                  <h4>Date Filters</h4>

                  <div className="filter-group">
                    <label>Created Date Range:</label>
                    <div className="date-range">
                      <input
                        type="date"
                        value={filters.createdAfter}
                        onChange={(e) => handleFilterChange('createdAfter', e.target.value)}
                      />
                      <span className="range-separator">to</span>
                      <input
                        type="date"
                        value={filters.createdBefore}
                        onChange={(e) => handleFilterChange('createdBefore', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="filter-group">
                    <label>Updated Date Range:</label>
                    <div className="date-range">
                      <input
                        type="date"
                        value={filters.updatedAfter}
                        onChange={(e) => handleFilterChange('updatedAfter', e.target.value)}
                      />
                      <span className="range-separator">to</span>
                      <input
                        type="date"
                        value={filters.updatedBefore}
                        onChange={(e) => handleFilterChange('updatedBefore', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="filters-actions">
                <button className="btn-secondary" onClick={() => setShowFilters(false)}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={applyFilters}>
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="supporting-beliefs-content">
        <SupportingBeliefsListView
          filters={listViewFilters}
          showCheckboxes={true}
          showBulkActions={true}
          compact={false}
        />
      </div>
    </div>
  );
};

export default SupportingBeliefs;
